import {AfterViewInit, Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {LoginService} from "../login/login.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {AuthResponseData} from "../login/auth-response-data.model";
import {TranslationService} from "../../services/translation.service";
import {isPlatformBrowser} from "@angular/common";
import {SsoConfiguration} from "../login/sso-configuration";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {HttpClient, HttpParams} from "@angular/common/http";
import {NavigationCommand, NavigationService} from '../../../data/services/navigation.service';

@Component({
  selector: 'app-login-formular',
  templateUrl: './login-formular.component.html',
  styleUrls: ['./login-formular.component.scss']
})
export class LoginFormularComponent implements OnInit, AfterViewInit {
  loginForm: FormGroup;
  loading: boolean = false;
  redirecting: boolean = false;
  error: string;
  returnUrl: string = null;
  isAuthenticated: boolean = false;
  authSub: Subscription;
  authData: AuthResponseData = null;

  ssoConfiguration: SsoConfiguration;
  useHassoForm = false;
  hassoLoginUrl: SafeUrl = null;
  prefillUsername = '';
  assignClientNo = 0;
  public ssoFormPropagatedHeight = '';

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private httpClient: HttpClient,
    private loginService: LoginService,
    private sanitizer: DomSanitizer,
    private navigationService: NavigationService,
    public translationService: TranslationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.ssoConfiguration = loginService.getSsoConfiguration()
    this.useHassoForm = this.ssoConfiguration.usage?.toUpperCase().startsWith('OAUTHHASSO');

    this.route?.queryParams?.subscribe((params) => {
      if (params?.prefillUsername) {
        this.prefillUsername = params.prefillUsername;
      }
      if(params?.clientNo) {
        this.assignClientNo = params.clientNo
      }
      if (params?.returnUrl) {
        this.returnUrl = params.returnUrl;
        this.handleRedirect();
      }
    });

    this.authSub = this.loginService?.getAuthData()?.subscribe((authData) => {
      this.authData = authData;
      this.isAuthenticated = !!authData;
      if(this.isAuthenticated && this.assignClientNo > 0) {
        this.redirecting = true;
        loginService.assignClientNo(this.assignClientNo).subscribe({
          next: res=> this.handleRedirect(),
          error: err => this.handleRedirect()
        });
      } else {
        this.handleRedirect();
      }
    });

    if (this.useHassoForm && isPlatformBrowser(this.platformId)) {
      window.addEventListener("message", (event) => {
          this.loading = false;
          const eventData = typeof event.data == 'string' ? JSON.parse(event.data) : event.data;

          if (eventData.eventNature == 'ssoAjaxViewLoaded' || eventData.eventNature == 'ssoAjaxViewChanged') {
            this.ssoFormPropagatedHeight = eventData.formHeight ? (eventData.formHeight + 5) + 'px' : 'inherit';
          } else {
            const alreadyLoggedIn = eventData.code === 2000;
            const justLoggedIn = typeof eventData.code == 'string' && eventData.code.match(/^[0-9a-f]{16,}$/);
            if (alreadyLoggedIn || justLoggedIn) {
              this.loginService.checkStatus();
            }
          }
        },
        false,
      );
    }
  }

  ngAfterViewInit(): void {
    if (this.useHassoForm && isPlatformBrowser(this.platformId)) {
      // show iframe source regardless of consent-manager-setting
      // this should be irrelevant at production environment, but is super useful before (especially DEV)
      const iframe = document.querySelector('#ssoLoginFrame[src=""][data-cmp-src]');
      iframe?.setAttribute('src', iframe.attributes.getNamedItem('data-cmp-src').nodeValue);
    }
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder?.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required]
    });

    if (this.useHassoForm && isPlatformBrowser(this.platformId)) {
      this.loading = true;

      let returnUri = this.ssoConfiguration.returnUrl;
      if (!!this.navigationService.getPreviousUrl()) {
        returnUri = location.origin + this.navigationService.getPreviousUrl();
      }

      let loginBoxParams = {
        client_id: this.ssoConfiguration.clientId,
        redirect_uri: encodeURIComponent(returnUri),
        responsetype: 'event',
        domain: encodeURIComponent(location.origin),
        ajaxMode: 1,
        formId: 'Login',
      };
      if (this.prefillUsername.length) {
        loginBoxParams['uSrNaMe'] = this.prefillUsername;
      }

      this.hassoLoginUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.ssoConfiguration.ssoHost + '/auth/authorize?' + new HttpParams({fromObject: loginBoxParams}).toString()
      );
    }
  }

  onLogin(): void {
    if (this.loginForm?.valid) {
      this.loading = true;
      this.loginService.login(this.loginForm.get('email').value, this.loginForm.get('password').value, this.returnUrl).subscribe((res) => {
        if (res && res.token !== 0 && res.status !== 'error') {
        } else {
          this.error = res.message;
          this.loading = false;
        }
      }, error => {
        this.error = error.error;
        this.loading = false;
      });
    }
  }

  private handleRedirect(): void {
    this.redirecting = false;
    if (this.isAuthenticated && this.returnUrl) {
      this.router
        .navigateByUrl(this.returnUrl, {replaceUrl: true})
        .then(() => null);
    }
  }

  onBack(): void {
    history.back();
  }

}
